/* eslint-disable prettier/prettier */
/* eslint-disable indent */
// Modules
import React, { useState, ChangeEvent } from 'react'
import { useFormik } from 'formik'
import { formatCPF } from '@brazilian-utils/brazilian-utils'
import { useHistory, useLocation } from 'react-router-dom'

// Application
import { UserLogin } from 'redesign/validators'
import { useAuthentication } from 'redesign/hooks/useAuthentication'
import { ROUTES_PATH } from 'constants/routesPath'
import { useCurrentLaboratory } from '../useCurrentLaboratory'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'
import { useSignUpAnaytics } from 'hooks/useSignUpAnaytics'
import { federalTaxIdValidator } from 'utils'
import { useRecoverHook } from 'hooks'
import { snackbarType } from 'redesign/typings/snackbar'

type GeolocationPositionType = {
    coords: {
        latitude: number | null
        longitude: number | null
    }
}

export const useCPFLoginForm = () => {
    const history = useHistory()
    const location = useLocation()
    const recover = useRecoverHook()
    const { isFeatureEnabled } = useRemoteConfig()
    const { handleAuthUser, hasValidatedContact, failedPasswordCount, isLogin, setIsLogin } = useAuthentication()
    const { title } = useCurrentLaboratory()
    const [viewPassword, setViewPassword] = useState(false)

    const { dispatchAnalyticsEventFeedBack } = useSignUpAnaytics()

    const initialValues = {
        cpf: '',
        password: '',
        showPasswordField: false,
        keepLogged: true,
        latitude: null,
        longitude: null,
        address: null,
    }

    const form = useFormik({
        initialValues,
        validationSchema: UserLogin,
        onSubmit: async (formValues, { setFieldError, setSubmitting, setFieldValue, setTouched }) =>
            handleAuthUser({ ...formValues, setFieldError, setSubmitting, setFieldValue, setTouched }),
    })

    const handleViewPassword = () => {
        setViewPassword(!viewPassword)
    }

    const resetForm = () => {
        form.setFieldValue('password', '')
        form.setFieldValue('showPasswordField', false)
        setIsLogin(true)
    }

    const redirectToLoginProtocol = () => {
        const params = new URLSearchParams(location.search)
        params.set('phased-login-origin', 'true')
        history.push({
            ...location,
            pathname: ROUTES_PATH.LOGIN,
            search: params.toString(),
        })
    }

    const redirectToLogin = (e: React.MouseEvent) => {
        e.preventDefault()
        const params = new URLSearchParams(location.search)
        params.delete('phased-login-origin')

        history.push({
            ...location,
            pathname: ROUTES_PATH.SIGN_IN,
            search: params.toString(),
        })
    }
    const redirectToForgotPassword = (cpf: string) => {
        recover.setUsername(cpf)
        history.push(ROUTES_PATH.RECOVER_PASSWORD, { federalId: cpf })
    }

    const handleChangeCpf = (event: ChangeEvent<HTMLInputElement>) => {
        const currentValue = event.target.value.replace(/\./g, '').replace(/-/g, '')
        event.target.value = formatCPF(currentValue)
        form.handleChange(event)
        if (currentValue.length === 4) {
            dispatchAnalyticsEventFeedBack('confirmCpf', { errorMessage: 'Interagiu com campo CPF' })
        }
        if (currentValue.length === 11 && !federalTaxIdValidator(event.target.value)) {
            dispatchAnalyticsEventFeedBack('confirmCpf')
        }
    }

    const isAuthServiceDown = () => {
        const isServiceDown = isFeatureEnabled('AUTH_SERVICE_DOWN')

        return { isServiceDown }
    }

    const retryGeolocation = (
        setIsCheckDisable: (value: boolean) => void,
        openSnackbar: ({ variant, message }: snackbarType) => void,
        onCloseSnackbar: () => void,
    ) => {
        onCloseSnackbar()
        getGeolocation(setIsCheckDisable, openSnackbar, onCloseSnackbar, true)
    }

    const getGeolocation = (
        setIsCheckDisable: (value: boolean) => void,
        openSnackbar: ({ variant, message }: snackbarType) => void,
        onCloseSnackbar: () => void,
        retry?: boolean,
    ) => {
        const { geolocation } = navigator

        const setGeolocation = (position: GeolocationPositionType) => {
            form.setFieldValue('latitude', String(position.coords.latitude))
            form.setFieldValue('longitude', String(position.coords.longitude))
        }

        const notSuported = () => {
            openSnackbar({
                variant: 'error',
                message: 'Seu browser não suporta geolocalização, você não conseguirá manter-se logado para os próximos acessos',
            })
            setIsCheckDisable(true)
            form.setFieldValue('keepLogged', false)
        }

        const error = () => {
            retry
                ? openSnackbar({
                      variant: 'simple',
                      duration: 10000,
                      message: 'Para permanecer logado, você precisa permitir o acesso à localização nas configurações do navegador.',
                  })
                : openSnackbar({
                      variant: 'error',
                      duration: 10000,
                      message: 'Precisamos de sua localização para te manter logado.',
                      actionButton: {
                          onClick: () => {
                              retryGeolocation(setIsCheckDisable, openSnackbar, onCloseSnackbar)
                          },
                          label: 'Permitir localização',
                      },
                  })
            form.setFieldValue('keepLogged', false)
        }

        geolocation ? geolocation.getCurrentPosition(setGeolocation, error) : notSuported()
    }

    const getIP = () => {
        fetch('https://api.ipify.org/?format=json')
            .then(response => response.json())
            .then(data => {
                const { ip } = data
                form.setFieldValue('address', ip)
            })
    }

    return {
        ...form,
        hasValidatedContact,
        failedPasswordCount,
        title,
        viewPassword,
        isLogin,
        isServiceDown: isAuthServiceDown().isServiceDown,
        handleChangeCpf,
        redirectToLogin,
        redirectToLoginProtocol,
        handleViewPassword,
        redirectToForgotPassword,
        isAuthServiceDown,
        resetForm,
        getGeolocation,
        getIP,
    }
}
